/**
 * The species context the API hands back at login.
 *
 * PedigreeSmart is generic across species, but every tenant sees exactly one
 * specialised application: a cynological association reads "Cani",
 * "Cucciolata", "Fattrice"; an equestrian one reads "Cavalli", "Puledro",
 * "Scuderia". None of that is hard-coded anywhere in the UI — it all comes
 * from here.
 *
 * Deliberately free of any DOM or React dependency, so a future React Native
 * app can import this package unchanged.
 */

export type SpeciesCode = 'canine' | 'feline' | 'equine' | 'bovine' | 'lagomorph' | 'avian' | (string & {})

export type AttributeType =
  | 'string'
  | 'text'
  | 'integer'
  | 'decimal'
  | 'boolean'
  | 'date'
  | 'enum'
  | 'file'

export interface SpeciesAttribute {
  key: string
  label: string
  type: AttributeType
  group?: string
  unit?: string
  options?: readonly string[]
  required: boolean
  searchable: boolean
  in_certificate: boolean
}

export interface SpeciesRules {
  pedigree_generations: number
  multiple_birth: boolean
  inbreeding_generations?: number
  identifier_required?: string
  gestation_days?: number
  gestation_tolerance_days?: number
  min_breeding_age_months?: number
  cross_breed_allowed?: boolean
}

/**
 * Domain vocabulary. Not i18n: these differ between species within the same
 * language, which is why they live in data rather than in translation files.
 */
export interface SpeciesTerminology {
  animal?: string
  'animal.plural'?: string
  'animal.young'?: string
  'animal.young.plural'?: string
  litter?: string
  dam?: string
  sire?: string
  breeder?: string
  kennel?: string
  breed?: string
  show?: string
  working_trial?: string
  registry_number?: string
  [key: string]: string | undefined
}

export interface SpeciesContext {
  code: SpeciesCode
  name: string
  is_default: boolean
  terminology: SpeciesTerminology
  rules: SpeciesRules
  attributes: readonly SpeciesAttribute[]
}

/** Fallback labels, used when a species leaves a term undefined. */
const GENERIC_TERMS: SpeciesTerminology = {
  animal: 'Soggetto',
  'animal.plural': 'Soggetti',
  'animal.young': 'Nato',
  'animal.young.plural': 'Nati',
  litter: 'Nidiata',
  dam: 'Madre',
  sire: 'Padre',
  breeder: 'Allevatore',
  kennel: 'Allevamento',
  breed: 'Razza',
  show: 'Manifestazione',
  working_trial: 'Prova',
  registry_number: 'Numero di registro',
}

/**
 * Resolves a domain term for a species.
 *
 * Falls back to the generic label, then to the key itself — a missing term
 * shows up as `animal.young` rather than as an empty string, so it gets
 * noticed and fixed.
 */
export function resolveTerm(species: SpeciesContext | null, key: string): string {
  return species?.terminology[key] ?? GENERIC_TERMS[key] ?? key
}

export function findAttribute(
  species: SpeciesContext | null,
  key: string,
): SpeciesAttribute | undefined {
  return species?.attributes.find((attribute) => attribute.key === key)
}

/** Attributes grouped for display, preserving the server-side ordering. */
export function groupAttributes(
  species: SpeciesContext | null,
): ReadonlyArray<{ group: string; attributes: readonly SpeciesAttribute[] }> {
  if (!species) return []

  const groups = new Map<string, SpeciesAttribute[]>()

  for (const attribute of species.attributes) {
    const group = attribute.group ?? 'altro'
    const existing = groups.get(group)

    if (existing) {
      existing.push(attribute)
    } else {
      groups.set(group, [attribute])
    }
  }

  return [...groups].map(([group, attributes]) => ({ group, attributes }))
}

export function searchableAttributes(species: SpeciesContext | null): readonly SpeciesAttribute[] {
  return species?.attributes.filter((attribute) => attribute.searchable) ?? []
}
